import React from 'react'
import { inject, observer } from 'mobx-react'
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued'
import { message, Row, Spin } from 'antd-v4'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { BlazeButton } from '../../../../components/CustomButtons/BlazeButton'
import moment from 'moment'
import { LocalStorage } from '../../../../utilities/LocalStorage'
import { EventsV2 } from '@getgreenline/events'
import { DateFilter } from '../../DashboardReports/Financial/ReportFilter/DateFilter'
import { Timeline, Empty, Typography } from 'antd'
import { LoadingAnimation } from '../../../../components/LoadingAnimation'
import { COLORS } from '../../../../constants/Colors'
import EndPoints from '../../../../constants/Endpoints'
import { ProductLog } from './ProductLog'
import { parseErrorMsg, getPopupContainer } from '../../../../utilities/helpers'
import { productApi, ProductModels } from '@getgreenline/products'
import { CreateChildProduct, CreateProduct } from './ProductStore'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  product: CreateProduct | CreateChildProduct
}

interface State {
  viewOldEditHistory: boolean
  logs: ProductModels.IProductEventLogData[]
  isLoading: boolean
  isExtraLoading: boolean
  hasMore: boolean
  startDate: moment.Moment
  endDate: moment.Moment
  nextToken?: string
  queryExecutionId?: string
}

@inject('currentCompanyStore')
@observer
export class ProductEventLogs extends React.Component<Props, State> {
  tenantId = this.props.currentCompanyStore!.company!.id

  limit = 10

  constructor(props: Props) {
    super(props)

    const productStartDate = moment(props.product.createDate).startOf('day')
    const oneYearAgo = moment().subtract(1, 'year').startOf('day')
    const timeDiffInSecs = moment.duration(productStartDate.diff(oneYearAgo)).asSeconds()
    const startDateUpToAYearAgo = timeDiffInSecs > 0 ? productStartDate : oneYearAgo

    this.state = {
      viewOldEditHistory: true,
      logs: [],
      isLoading: true,
      isExtraLoading: false,
      hasMore: false,
      startDate: startDateUpToAYearAgo,
      endDate: moment().endOf('day'),
      nextToken: undefined,
      queryExecutionId: undefined,
    }
  }

  componentDidMount = async () => {
    const cachedParams = LocalStorage.getEventLogParams(
      EventsV2.EventModels.EventTopics.PRODUCT,
      this.localStorageKey,
    )

    const timeRangeChanged = this.isTimeRangeChanged(cachedParams?.startDate, cachedParams?.endDate)

    if (!timeRangeChanged) {
      await this.setState({ queryExecutionId: cachedParams?.queryExecutionId })
    }

    await this.fetchLogs()
  }

  componentDidUpdate = async (prevProps: Props, prevState: State) => {
    const strStartDate = prevState.startDate.toISOString()
    const strEndDate = prevState.endDate.toISOString()

    const timeRangeChanged = this.isTimeRangeChanged(strStartDate, strEndDate)

    if (timeRangeChanged) {
      await this.fetchLogs()
    }
  }

  isTimeRangeChanged = (startDate?: string, endDate?: string) => {
    const startDateChanged = startDate !== this.state.startDate.toISOString()
    const endDateChanged = endDate !== this.state.endDate.toISOString()

    return startDateChanged || endDateChanged
  }

  get localStorageKey() {
    return JSON.stringify({
      tenantId: this.tenantId,
      productId: this.props.product.id,
    })
  }

  getLogs = async () => {
    if (this.props.product.id === null) return

    const { data, pagination } = await productApi.getProductEventLogs({
      gateway: EndPoints.AUTHZ_GATEWAY_URL,
      tenantId: `${this.tenantId}`,
      query: {
        startDate: this.state.startDate.toISOString(),
        endDate: this.state.endDate.toISOString(),
        nextToken: this.state.nextToken,
        queryExecutionId: this.state.queryExecutionId,
        productId: this.props.product.id,
        limit: this.limit,
      },
    })

    this.setState({ hasMore: data.length === this.limit })

    const value = JSON.stringify({
      queryExecutionId: pagination.queryExecutionId,
      startDate: this.state.startDate.toISOString(),
      endDate: this.state.endDate.toISOString(),
    })

    LocalStorage.setEventLogParams(
      EventsV2.EventModels.EventTopics.PRODUCT,
      this.localStorageKey,
      value,
    )

    this.setState({
      nextToken: pagination.nextToken,
      queryExecutionId: pagination.queryExecutionId,
      logs: [...this.state.logs, ...data],
    })
  }

  stringifyLog = (log: ProductModels.IProductEventLogData) => {
    const { id, updateDate, detailType, userName, date, ...restOfLog } = log

    return JSON.stringify(restOfLog, null, 1)
  }

  fetchLogs = async () => {
    try {
      await this.setState({ isLoading: true })

      await this.getLogs()
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while loading edit history')
    } finally {
      this.setState({ isLoading: false })
    }
  }

  refreshLogs = async () => {
    try {
      await this.setState({
        isLoading: true,
        nextToken: undefined,
        queryExecutionId: undefined,
        logs: [],
      })

      await this.getLogs()
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while refreshing edit history')
    } finally {
      this.setState({
        isLoading: false,
      })
    }
  }

  loadNextLogSet = async () => {
    try {
      await this.setState({ isExtraLoading: true })

      await this.getLogs()
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while loading more edit history')
    } finally {
      this.setState({ isExtraLoading: false })
    }
  }

  onStartDateChange = (startDate: Date | null) => {
    if (!startDate) return

    this.setState({
      startDate: moment(startDate),
      nextToken: undefined,
      queryExecutionId: undefined,
      logs: [],
    })
  }

  onEndDateChange = (endDate: Date | null) => {
    if (!endDate) return

    this.setState({
      endDate: moment(endDate),
      nextToken: undefined,
      queryExecutionId: undefined,
      logs: [],
    })
  }

  getEventAction = (log: ProductModels.IProductEventLogData): EventsV2.EventModels.EventTypes => {
    const [resource, action] = log.detailType.split('.')

    return action as EventsV2.EventModels.EventTypes
  }

  getEventColor = (log: ProductModels.IProductEventLogData) => {
    const action = this.getEventAction(log)

    let eventColor = COLORS.BRAND.retail

    if (action === EventsV2.EventModels.GreenlineEventTypes.PRODUCT.CREATED) {
      eventColor = COLORS.BRAND.grow
    }
    if (action === EventsV2.EventModels.GreenlineEventTypes.PRODUCT.DELETED) {
      eventColor = COLORS.ACTIONS.danger
    }

    return eventColor
  }

  render() {
    const timeDiffInMonths = Math.floor(
      moment.duration(this.state.endDate.diff(this.state.startDate)).asMonths(),
    )

    let spinTip = `Please wait while retrieving logs since ${timeDiffInMonths} month(s) ago`

    if (timeDiffInMonths >= 12) {
      spinTip = `Please wait while retrieving logs since ${timeDiffInMonths / 12} year(s) ago`
    } else if (timeDiffInMonths === 0) {
      spinTip = `Please wait while retrieving logs`
    }

    return (
      <>
        <div className='mb-2'>
          <span>Click </span>
          <BlazeButton
            buttonProps={{
              loading: this.state.isExtraLoading,
              onClick: () => this.setState({ viewOldEditHistory: !this.state.viewOldEditHistory }),
              type: 'link',
              style: { padding: 0 },
            }}
            titleProps={{
              title: 'here',
            }}
          />
          {this.state.viewOldEditHistory ? (
            <span> to view debugging version of edit history</span>
          ) : (
            <span>
              {' '}
              to view current version of edit history and data <b>prior to March 9, 2023</b>
            </span>
          )}
        </div>

        {this.state.viewOldEditHistory ? (
          <ProductLog
            companyId={this.props.currentCompanyStore!.company!.id}
            productId={this.props.product.id || undefined}
          />
        ) : (
          <>
            <div className='d-flex flex-wrap space-evenly pb-2 pr-0'>
              <DateFilter
                wrapperClassName='gr-filter-item gr-filter-gutter'
                wrapperStyle={{ flexGrow: 1 }}
                startDate={this.state.startDate.toDate()}
                endDate={this.state.endDate.toDate()}
                onStartDateChange={this.onStartDateChange}
                onEndDateChange={this.onEndDateChange}
                getPopupContainer={getPopupContainer}
                allowClear={false}
                presets={{
                  'A month ago': [
                    moment().subtract(1, 'month').startOf('day'),
                    moment().endOf('day'),
                  ],
                  '2 months ago': [
                    moment().subtract(2, 'month').startOf('day'),
                    moment().endOf('day'),
                  ],
                  '6 months ago': [
                    moment().subtract(6, 'month').startOf('day'),
                    moment().endOf('day'),
                  ],

                  'A year ago': [
                    moment().subtract(1, 'year').startOf('day'),
                    moment().endOf('day'),
                  ],
                  '2 years ago (slow)': [
                    moment().subtract(2, 'year').startOf('day'),
                    moment().endOf('day'),
                  ],
                }}
              />

              <Row className='gr-filter-gutter'>
                <BlazeButton
                  buttonProps={{
                    loading: this.state.isLoading,
                    onClick: this.refreshLogs,
                    icon: 'reload',
                  }}
                />
              </Row>

              <Row className='gr-filter-gutter'>
                <BlazeButton
                  buttonProps={{
                    className: 'w-100',
                    disabled: !this.state.hasMore,
                    loading: this.state.isExtraLoading,
                    onClick: this.loadNextLogSet,
                  }}
                  titleProps={{
                    title: 'Load more',
                  }}
                />
              </Row>
            </div>

            <div
              className='pt-1'
              style={{
                maxHeight: 800,
                overflow: 'auto',
              }}
            >
              <Timeline className='pl-2'>
                {this.state.isLoading && (
                  <Spin tip={spinTip}>
                    <div className='content' style={{ height: 80 }} />
                  </Spin>
                )}

                {!this.state.isLoading && this.state.logs.length === 0 && (
                  <Empty
                    description={
                      <span>
                        No edit history in <b>selected</b> time range
                      </span>
                    }
                    style={{ paddingBottom: 20 }}
                  />
                )}

                {this.state.logs.map((log, index) => {
                  const nextLog =
                    index === this.state.logs.length - 1 ? undefined : this.state.logs[index + 1]

                  const eventAction = this.getEventAction(log)

                  /**
                   * The oldest log in the array has nothing to compare to. Don't show it untill another set of logs is loaded.
                   */
                  if (
                    nextLog === undefined &&
                    eventAction !== EventsV2.EventModels.EventTypes.CREATED
                  ) {
                    return
                  }

                  return (
                    <Timeline.Item key={index} color={this.getEventColor(log)}>
                      {!this.props.product.id && <div>{log.name}</div>}

                      <div className='mb-1'>
                        <Typography.Text>
                          {eventAction} by {log.userName} on{' '}
                          {moment(log.updateDate).format('MMM D, YYYY h:mma')}
                        </Typography.Text>
                      </div>

                      {this.state.logs.length > 1 && nextLog && (
                        <Row style={{ paddingTop: 18, flex: 1 }}>
                          <ReactDiffViewer
                            compareMethod={DiffMethod.TRIMMED_LINES}
                            hideLineNumbers={true}
                            splitView={false}
                            showDiffOnly={true}
                            extraLinesSurroundingDiff={1}
                            oldValue={this.stringifyLog(nextLog)}
                            newValue={this.stringifyLog(log)}
                          />
                        </Row>
                      )}
                    </Timeline.Item>
                  )
                })}
              </Timeline>
            </div>

            <div className='d-flex flex-wrap'>
              <BlazeButton
                buttonProps={{
                  className: 'w-100',
                  disabled: !this.state.hasMore,
                  loading: this.state.isExtraLoading,
                  onClick: this.loadNextLogSet,
                }}
                titleProps={{
                  title: 'Load more',
                }}
              />
            </div>
          </>
        )}
      </>
    )
  }
}
